import { useState, useCallback, useMemo } from "react";
import { handleActionError } from "@/lib/errors";
import {
  useApplyPromoCode,
  useRemovePromoCode
} from "@/queries/cart/mutations";

interface PromoCodeState {
  /** Map of cart ID to applied promo code */
  appliedCodes: Record<string, string>;
  /** Map of cart ID to promo error message */
  errors: Record<string, string>;
}

export interface UseCartPromoCodesReturn {
  /** Map of cart ID to applied promo code */
  appliedPromoCodes: Record<string, string>;
  /** Map of cart ID to promo error message */
  promoErrors: Record<string, string>;
  /** Apply a promo code to a cart */
  applyPromo: (cartId: string, code: string) => Promise<void>;
  /** Remove a promo code from a cart */
  removePromo: (cartId: string) => Promise<void>;
  /** Check if a cart has a promo code being applied */
  isApplying: (cartId: string) => boolean;
}

/**
 * Hook for managing promo codes across multiple seller carts.
 *
 * Handles:
 * - Applying promo codes to individual carts
 * - Removing promo codes from carts
 * - Error state per cart
 * - Loading state per cart
 * - Query invalidation on success (via mutations)
 *
 * @example
 * ```tsx
 * const { appliedPromoCodes, promoErrors, applyPromo, removePromo } = useCartPromoCodes();
 *
 * <OrderSummary
 *   onApplyPromo={applyPromo}
 *   onRemovePromo={removePromo}
 *   appliedPromoCodes={appliedPromoCodes}
 *   promoErrors={promoErrors}
 * />
 * ```
 */
export function useCartPromoCodes(): UseCartPromoCodesReturn {
  const [state, setState] = useState<PromoCodeState>({
    appliedCodes: {},
    errors: {}
  });

  // Use TanStack Query mutations for promo code operations
  const applyPromoMutation = useApplyPromoCode();
  const removePromoMutation = useRemovePromoCode();

  const applyPromo = useCallback(
    async (cartId: string, code: string) => {
      // Clear any previous error
      setState((prev) => ({
        ...prev,
        errors: { ...prev.errors, [cartId]: "" }
      }));

      try {
        await applyPromoMutation.mutateAsync({ cartId, code });

        // Success - store the applied code
        setState((prev) => ({
          ...prev,
          appliedCodes: { ...prev.appliedCodes, [cartId]: code }
        }));
      } catch (error) {
        // The salon typed this code, so the API's verdict on it ("not found",
        // "has expired") is the message, whatever 4xx it arrives as; a 5xx or no
        // answer gets generic copy and is reported.
        const errorMessage = handleActionError(error, {
          boundary: "promo code apply",
          fallback:
            "That promo code couldn't be applied. Please check it and try again.",
          trustClientErrors: true
        });

        setState((prev) => ({
          ...prev,
          errors: { ...prev.errors, [cartId]: errorMessage }
        }));

        throw error;
      }
    },
    [applyPromoMutation]
  );

  const removePromo = useCallback(
    async (cartId: string) => {
      const appliedCode = state.appliedCodes[cartId];
      if (!appliedCode) return;

      try {
        await removePromoMutation.mutateAsync({ cartId, code: appliedCode });

        // Success - remove the applied code
        setState((prev) => {
          const { [cartId]: _, ...remainingCodes } = prev.appliedCodes;
          return {
            ...prev,
            appliedCodes: remainingCodes
          };
        });
      } catch (error) {
        const errorMessage = handleActionError(error, {
          boundary: "promo code remove",
          fallback: "We couldn't remove that promo code. Please try again."
        });

        setState((prev) => ({
          ...prev,
          errors: { ...prev.errors, [cartId]: errorMessage }
        }));

        throw error;
      }
    },
    [removePromoMutation, state.appliedCodes]
  );

  const isApplying = useCallback(
    (cartId: string) => {
      // Check if mutations are pending for this cart
      return (
        (applyPromoMutation.isPending &&
          applyPromoMutation.variables?.cartId === cartId) ||
        (removePromoMutation.isPending &&
          removePromoMutation.variables?.cartId === cartId)
      );
    },
    [applyPromoMutation, removePromoMutation]
  );

  return useMemo(
    () => ({
      appliedPromoCodes: state.appliedCodes,
      promoErrors: state.errors,
      applyPromo,
      removePromo,
      isApplying
    }),
    [state.appliedCodes, state.errors, applyPromo, removePromo, isApplying]
  );
}
